import { decode } from "base64-arraybuffer";
import { ApiClient } from "client/base";
import { isAzureAccount, isGCPAccount } from "types/account";
import { isObject } from "types/base";
import { delay } from "utils/promise";
import * as lz4 from "lz4js";
const CHUNK_SIZE = 1024 * 1024 * 4;
const isAzureResponse = (x) => {
    return isObject(x) && "body" in x;
};
const isBase64Gzip = (x) => {
    if (x.length < 3) {
        return false;
    }
    return isGzipBytes(decode(x.slice(0, 3)));
};
export const isGzipBytes = (array) => {
    const bytes = new Uint8Array(array);
    return bytes[0] == 31 && bytes[1] == 139; // gzip file signature: 1F 8B
};
export const compressFile = (file, compression = "GZIP") => {
    return compressBlob(file, compression).then((blob) => new File([blob], file.name));
};
export const isGzip = (blob) => {
    return blob
        .slice(0, 3)
        .arrayBuffer()
        .then((buffer) => isGzipBytes(buffer));
};
const compressBlob = (blob, compression = "GZIP") => {
    return isGzip(blob).then((isGzip) => {
        if (isGzip) {
            return blob;
        }
        if (compression == "LZ4") {
            return blob
                .arrayBuffer()
                .then((buffer) => new Blob([lz4.compress(new Uint8Array(buffer))], { type: "application/x-lz4" }));
        }
        const ds = new window.CompressionStream("gzip");
        return new Response(blob.stream().pipeThrough(ds)).blob();
    });
};
const decompressGzipBlob = (blob) => {
    return isGzip(blob).then((check) => {
        if (!check) {
            return blob;
        }
        const DS = window.DecompressionStream;
        const ds = new DS("gzip");
        const decompressedStream = blob.stream().pipeThrough(ds);
        const response = new Response(decompressedStream);
        return response.blob().then((b) => isGzip(b).then((check) => (check ? decompressGzipBlob(b) : b)));
    });
};
const chunksToJson = (chunks) => {
    if (chunks.length == 0) {
        throw Error("array of chunks must have length greater than 0");
    }
    const type = isBase64Gzip(chunks[0]) ? "application/gzip" : "text";
    if (type == "text") {
        return JSON.parse(chunks.join());
    }
    else if (type == "application/gzip") {
        // assume chunks are base64 encoded
        // check for padding in all the chunks except the last
        if (chunks.length > 1 && chunks.slice(0, -1).every((x) => !x.endsWith("="))) {
            chunks = [chunks.join("")]; // no padding means we can decode everything at once
        }
        const blob = new Blob(chunks.map((x) => new Uint8Array(decode(x))), { type });
        return decompressGzipBlob(blob)
            .then((blob) => blob.text())
            .then((text) => JSON.parse(text));
    }
    throw Error(`Unexpected type: ${type}`);
};
export class CloudMetaClient extends ApiClient {
    account;
    constructor(base, account) {
        super(base);
        this.account = account;
    }
    getMonitorResults(id) {
        if (isAzureAccount(this.account) || isGCPAccount(this.account)) {
            return this.getMonitorResultsSync(id);
        }
        else {
            return this.getMonitorResultsAsync(id);
        }
    }
    getMonitorResultsSync(id) {
        const params = {
            action: "MonitoringQuerySync",
            project_id: id
        };
        return this.getMeta(params);
    }
    getMonitorResultsAsync(id) {
        const params = {
            action: "MonitoringQueryPost",
            project_id: id
        };
        return this.getMeta(params).then(() => delay(2000).then(() => this.pollMonitorResults(id)));
    }
    pollMonitorResults(id) {
        const params = {
            action: "MonitoringQueryGet",
            project_id: id
        };
        return this.getMeta(params).then((result) => {
            if (result.status == "INPROGRESS") {
                return delay(2000).then(() => this.pollMonitorResults(id));
            }
            return result;
        });
    }
    getDatasetDetail(id) {
        const params = {
            action: "GetDatasetMetadataChunk",
            dataset_id: id
        };
        return this.getMeta(params);
    }
    getJobDetails(id) {
        const params = {
            action: "GetModelMetadataChunk",
            job_id: id
        };
        return this.getMeta(params);
    }
    getModelDetail(id, model_name) {
        const params = {
            action: "GetModelMetadataChunk",
            job_id: id,
            model_name: model_name
        };
        return this.getMeta(params);
    }
    getModelDetailList(id, model_names) {
        if (false && isAzureAccount(this.account)) {
            // Leave 500 character buffer for shenanigans
            const MAX_URL_LENGTH = 1500;
            // Factor in comma and quotations
            const QUERY_OFFSET = 9;
            const batchedQueries = [];
            let currQuery = [];
            let currLength = 0;
            model_names.forEach((name) => {
                if (currLength + name.length + QUERY_OFFSET > MAX_URL_LENGTH) {
                    batchedQueries.push(currQuery);
                    currQuery = [];
                    currLength = 0;
                }
                currQuery.push(name);
                currLength += name.length + QUERY_OFFSET;
            });
            batchedQueries.push(currQuery);
            return Promise.all(batchedQueries.map((modelArr) => {
                const params = {
                    action: "GetModelMetadataChunk",
                    job_id: id,
                    model_names: modelArr
                };
                return this.getMeta(params);
            })).then((models) => {
                return models.flat(1);
            });
        }
        const params = {
            action: "GetModelMetadataChunk",
            job_id: id,
            model_names: model_names
        };
        return this.getMeta(params);
    }
    getProjectAssumption(id) {
        return this.getProjectAssumptions([id]).then((assumptions) => assumptions[id]);
    }
    getProjectAssumptions(ids) {
        const params = {
            action: "GetProjectAssumptionChunk",
            ids: JSON.stringify(ids)
        };
        return this.getMeta(params);
    }
    getQueryPreview(relation) {
        const params = {
            action: "QueryPreview",
            relation: relation
        };
        return this.getMeta(params);
    }
    getValidateRelation(relation) {
        const params = {
            action: "ValidateRelation",
            relation: relation
        };
        return this.getMeta(params);
    }
    getMeta(params, offset = 0, size = CHUNK_SIZE) {
        return this.getChunkBody(params, offset, size).then((body) => {
            if (typeof body != "string") {
                return Promise.resolve(body);
            }
            return this.handleChunkResponse(body, params, offset + size, size).then((chunks) => chunksToJson(chunks));
        });
    }
    handleChunkResponse(data, params, offset = 0, size = CHUNK_SIZE) {
        const chunk = typeof data != "string" ? JSON.stringify(data) : data;
        if (chunk.length < size) {
            return Promise.resolve([chunk]);
        }
        return this.getChunkBody(params, offset, size)
            .then((body) => this.handleChunkResponse(body, params, offset + size, size))
            .then((chunks) => [chunk].concat(chunks));
    }
    getChunkBody(params, offset = 0, size = CHUNK_SIZE) {
        const chunkParams = {
            ...params,
            offset: offset,
            size: size
        };
        return this._getMeta(chunkParams);
    }
    _getMeta(params, retries = 0) {
        let promise;
        if (false && isAzureAccount(this.account)) {
            const searchParams = new URLSearchParams("");
            for (const [key, value] of Object.entries(params)) {
                searchParams.append(key, JSON.stringify(value));
            }
            promise = this.base.get(`v1/api/meta?${searchParams.toString()}`);
        }
        else {
            promise = this.base.post("v1/meta", params);
        }
        return promise
            .then((x) => (isAzureResponse(x) ? x.body : x))
            .catch((e) => {
            if (retries < 3) {
                return this._getMeta(params, retries + 1);
            }
            throw e;
        });
    }
}
